import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Card,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import authService from '../services/auth.service';

export default function ResetPasswordPage() {
  const theme = useTheme();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [targetUsername, setTargetUsername] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (!token) {
      setVerifying(false);
      setTokenValid(false);
      setTokenError('No password reset token was provided in the link. Please request a new link.');
      return;
    }

    authService.verifyResetToken(token)
      .then((res) => {
        if (!isMounted) return;
        setTokenValid(true);
        setTargetUsername(res.data?.username || '');
        setVerifying(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        setTokenValid(false);
        const msg = err.response?.data?.message || 'This password reset link is invalid or has expired. Please request a new one.';
        setTokenError(msg);
        setVerifying(false);
      });

    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!newPassword || newPassword.length < 6) {
      setFormError('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match. Please ensure both fields are identical.');
      return;
    }

    setSubmitting(true);

    try {
      await authService.resetPassword(token, newPassword);
      setResetSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.message || 'Password reset failed. Please request a new link and try again.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Reset Password | Mutual Trust Financial System</title>
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'stretch',
          backgroundColor: 'background.default',
        }}
      >
        {/* Left Side: Brand & Security Showcase */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: { md: '48%', lg: '45%' },
            position: 'relative',
            overflow: 'hidden',
            p: { md: 6, lg: 8 },
            color: '#FFFFFF',
            background: 'linear-gradient(145deg, #071C2E 0%, #0B3B60 55%, #054837 100%)',
            boxShadow: 'inset -1px 0 0 rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Ambient Glows */}
          <Box
            sx={{
              position: 'absolute',
              top: '-15%',
              left: '-15%',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(56, 189, 248, 0.18) 0%, rgba(11, 59, 96, 0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10%',
              right: '-10%',
              width: '450px',
              height: '450px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0) 70%)',
              pointerEvents: 'none',
            }}
          />

          {/* Top Header */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
              <Box
                component="img"
                src="/assets/icons/mtc.png"
                alt="Mutual Trust Logo"
                sx={{
                  width: 52,
                  height: 52,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.04em', lineHeight: 1.1 }}>
                  MUTUAL TRUST
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', letterSpacing: '0.08em', fontWeight: 600 }}>
                  COOPERATIVE MULTIPURPOSE SOCIETY
                </Typography>
              </Box>
            </Stack>

            <Chip
              label="🔑 Secure Password Reset"
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.75rem',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                mb: 3,
              }}
            />

            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.18,
                letterSpacing: '-0.02em',
                mb: 2.5,
                color: '#FFFFFF',
              }}
            >
              Choose a New Secure Password
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.82)',
                lineHeight: 1.6,
                maxWidth: 480,
                fontSize: '1rem',
              }}
            >
              Choose a strong, memorable password that is at least 6 characters in length. Once updated, your new password will take effect across all portal access points immediately.
            </Typography>
          </Box>

          {/* Security Best Practices */}
          <Box sx={{ position: 'relative', zIndex: 1, my: 4 }}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: '#FFFFFF',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#38BDF8' }}>
                💡 Password Security Recommendations:
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mb: 0.5 }}>
                • Minimum 6 characters (longer is always stronger)
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mb: 0.5 }}>
                • Mix letters, numbers, and symbols for best defense
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block' }}>
                • Never share your credentials with anyone
              </Typography>
            </Card>
          </Box>

          {/* Footer */}
          <Box sx={{ position: 'relative', zIndex: 1, pt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShieldOutlinedIcon sx={{ fontSize: 18, color: '#38BDF8' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 500 }}>
                Mutual Trust End-to-End Cryptographic Security • MTC-FS
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Right Side: Form / State Display */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 2.5, sm: 5, md: 8 },
            backgroundColor: 'background.paper',
            minHeight: '100vh',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 440 }}>
            {/* Top Navigation */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 3, sm: 4 } }}>
              <Button
                component={RouterLink}
                to="/login"
                startIcon={<ArrowBackRoundedIcon />}
                size="small"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { color: 'primary.main', backgroundColor: 'transparent' },
                }}
              >
                Back to Sign In
              </Button>

              <Chip
                label="Verified Channel"
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  borderColor: 'divider',
                  color: 'text.secondary',
                }}
              />
            </Stack>

            {/* Mobile Logo Display (visible on xs, hidden on md+) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box
                component="img"
                src="/assets/icons/mtc.png"
                alt="Mutual Trust Logo"
                sx={{ width: 42, height: 42, objectFit: 'contain' }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                  MUTUAL TRUST
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  COOPERATIVE SOCIETY
                </Typography>
              </Box>
            </Box>

            {/* State 1: Verifying Token */}
            {verifying && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress size={44} sx={{ color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#071C2E' }}>
                  Verifying Security Token...
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Please wait while we confirm your password reset link authenticity.
                </Typography>
              </Box>
            )}

            {/* State 2: Invalid or Expired Token */}
            {!verifying && !tokenValid && !resetSuccess && (
              <Box
                sx={{
                  textAlign: 'center',
                  p: { xs: 2.5, sm: 4 },
                  borderRadius: '16px',
                  backgroundColor: alpha(theme.palette.error.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    color: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px auto',
                  }}
                >
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 36 }} />
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: '#071C2E' }}>
                  Reset Link Expired or Invalid
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3.5, lineHeight: 1.6 }}>
                  {tokenError || 'This password reset link has already been used, was formatted incorrectly, or has exceeded its 60-minute security window.'}
                </Typography>

                <Stack spacing={1.5}>
                  <Button
                    component={RouterLink}
                    to="/forgot-password"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      py: 1.4,
                      borderRadius: '12px',
                      fontWeight: 700,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #0B3B60 0%, #0369A1 100%)',
                    }}
                  >
                    Request a New Reset Link
                  </Button>

                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="text"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                  >
                    Return to Login
                  </Button>
                </Stack>
              </Box>
            )}

            {/* State 3: Password Reset Successful */}
            {!verifying && resetSuccess && (
              <Box
                sx={{
                  textAlign: 'center',
                  p: { xs: 2.5, sm: 4 },
                  borderRadius: '16px',
                  backgroundColor: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.success.main, 0.12),
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px auto',
                  }}
                >
                  <CheckCircleOutlineRoundedIcon sx={{ fontSize: 36 }} />
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: '#071C2E' }}>
                  Password Reset Successfully!
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3.5, lineHeight: 1.6 }}>
                  Your account password has been updated and your credentials are now fully secure. You can now log in to the portal with your new password.
                </Typography>

                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    py: 1.4,
                    borderRadius: '12px',
                    fontWeight: 700,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #0B3B60 0%, #0369A1 100%)',
                    boxShadow: '0 8px 20px rgba(11, 59, 96, 0.28)',
                  }}
                >
                  Proceed to Sign In
                </Button>
              </Box>
            )}

            {/* State 4: Valid Token, Password Reset Form */}
            {!verifying && tokenValid && !resetSuccess && (
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: '10px',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      display: 'flex',
                    }}
                  >
                    <KeyRoundedIcon sx={{ fontSize: 26 }} />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#071C2E',
                      letterSpacing: '-0.02em',
                      fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
                    }}
                  >
                    Reset Password
                  </Typography>
                </Stack>

                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3.5, lineHeight: 1.6 }}>
                  {targetUsername
                    ? `Set a new secure password for account: `
                    : 'Enter and confirm your new account password below:'}
                  {targetUsername && (
                    <strong style={{ color: '#0B3B60' }}>{targetUsername}</strong>
                  )}
                </Typography>

                {formError && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>
                    {formError}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={2.5}>
                    {/* New Password */}
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.75, display: 'block' }}>
                        New Password
                      </Typography>
                      <TextField
                        fullWidth
                        size="medium"
                        placeholder="At least 6 characters"
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        autoFocus
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword((s) => !s)}
                                edge="end"
                                size="small"
                                sx={{ color: 'text.secondary' }}
                              >
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: alpha(theme.palette.grey[500], 0.04),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.grey[500], 0.08),
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                            },
                          },
                        }}
                      />
                    </Box>

                    {/* Confirm Password */}
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.75, display: 'block' }}>
                        Confirm New Password
                      </Typography>
                      <TextField
                        fullWidth
                        size="medium"
                        placeholder="Re-enter your new password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirmPassword((s) => !s)}
                                edge="end"
                                size="small"
                                sx={{ color: 'text.secondary' }}
                              >
                                {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: alpha(theme.palette.grey[500], 0.04),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.grey[500], 0.08),
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                            },
                          },
                        }}
                      />
                    </Box>

                    {/* Submit Button */}
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={submitting}
                      loadingIndicator={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress color="inherit" size={18} />
                          <Typography variant="button" sx={{ color: '#fff', fontSize: '0.9rem' }}>
                            Saving password...
                          </Typography>
                        </Stack>
                      }
                      sx={{
                        mt: 1,
                        py: 1.5,
                        borderRadius: '12px',
                        fontSize: '1rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #0B3B60 0%, #0369A1 100%)',
                        boxShadow: '0 8px 20px rgba(11, 59, 96, 0.28)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #072740 0%, #0284C7 100%)',
                          boxShadow: '0 12px 28px rgba(11, 59, 96, 0.38)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Update Password & Sign In
                    </LoadingButton>
                  </Stack>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
